import { FC, useState, useCallback, useRef, useEffect } from "react";
import { Field, useField, useFormikContext } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, Clock, X, ChevronUp, ChevronDown } from "lucide-react";
import { StyleFormWrapper, StyleInputWrapper, StyleLabel } from "../style";
import ErrorMessage from "../ErrorMessage";

// 🎨 Theme
export const orange = {
  orange50: "#fff7ed",
  orange100: "#ffedd5",
  orange200: "#fed7aa",
  orange300: "#fdba74",
  orange400: "#fb923c",
  orange500: "#f97316",
  orange600: "#ea580c",
  orange700: "#c2410c",
  orange800: "#9a3412",
  orange900: "#7c2d12",
} as const;

interface DateTimePickerProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  showTimeSelect?: boolean;
  showDateSelect?: boolean;
  dateFormat?: string;
  styleWrapper?: string;
  styleLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  timeIntervals?: number;
  timeFormat?: string;
  outputFormat?: "iso" | "numeric" | "formatted";
  timeCaption?: string;
}

const DateTimePicker: FC<DateTimePickerProps> = ({
  name,
  label,
  placeholder,
  required = false,
  showTimeSelect = false,
  showDateSelect: propShowDateSelect,
  dateFormat,
  styleWrapper = "",
  styleLabel = "",
  minDate,
  maxDate,
  disabled = false,
  timeIntervals = 15, // Changed default to 15 minutes for better granularity
  timeFormat = "HH:mm",
  outputFormat = "iso",
  timeCaption = "Time",
}) => {
  const showDateSelect = propShowDateSelect ?? !showTimeSelect;
  const [isFocus, setIsFocus] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const [field, meta] = useField(name);
  const datepickerRef = useRef<DatePicker>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeDropdownRef = useRef<HTMLDivElement>(null);

  const hasError = meta.touched && meta.error;

  // Determine placeholder based on props
  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    if (showDateSelect && showTimeSelect) return "Select date and time";
    if (showDateSelect) return "Select date";
    if (showTimeSelect) return "Select time";
    return "Select";
  };

  // Determine date format based on props
  const getDateFormat = () => {
    if (dateFormat) return dateFormat;

    if (showDateSelect && showTimeSelect) {
      return `MMMM d, yyyy ${timeFormat}`;
    } else if (showDateSelect) {
      return "MMMM d, yyyy";
    } else if (showTimeSelect) {
      return timeFormat;
    }
    return "MMMM d, yyyy";
  };

  // Format date for output based on outputFormat prop
  const formatDateForOutput = (date: any): string | null => {
    if (!date) return null;

    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
      console.error("Invalid date passed to formatDateForOutput:", date);
      return null;
    }

    if (showTimeSelect && !showDateSelect) {
      if (
        timeFormat?.toLowerCase().includes("aa") ||
        timeFormat?.toLowerCase().includes("hh")
      ) {
        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours || 12; // Handle 0 as 12
        return `${hours}:${minutes} ${ampm}`;
      }
      const hours = dateObj.getHours().toString().padStart(2, "0");
      const minutes = dateObj.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    }

    switch (outputFormat) {
      case "numeric":
        // Format: 2023/3/24 or 2023/3/24 14:30
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();

        if (showTimeSelect) {
          const hours = dateObj.getHours().toString().padStart(2, "0");
          const minutes = dateObj.getMinutes().toString().padStart(2, "0");
          return `${year}/${month}/${day} ${hours}:${minutes}`;
        }
        return `${year}/${month}/${day}`;

      case "formatted":
        // Use the same format as display
        return dateObj.toLocaleString();

      case "iso":
      default:
        return typeof dateObj.toISOString === "function"
          ? dateObj.toISOString()
          : dateObj.toString();
    }
  };

  // Parse input date based on outputFormat
  const parseInputDate = (value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date) return value;

    if (typeof value === "string") {
      // Try to parse numeric format: 2023/3/24 or 2023/3/24 14:30
      const numericMatch = value.match(
        /^(\d+)\/(\d+)\/(\d+)(?:\s+(\d+):(\d+))?$/,
      );
      if (numericMatch) {
        const [, year, month, day, hours, minutes] = numericMatch;
        const date = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          hours ? parseInt(hours) : 0,
          minutes ? parseInt(minutes) : 0,
        );
        return isNaN(date.getTime()) ? null : date;
      }

      // Try to parse "HH:mm" or "hh:mm aa" format (Time only)
      const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?:\s?(AM|PM|am|pm))?$/);
      if (timeMatch) {
        const [, hoursStr, minutesStr, meridian] = timeMatch;
        let hours = parseInt(hoursStr);
        const minutes = parseInt(minutesStr);

        if (meridian) {
          const isPM = meridian.toLowerCase() === "pm";
          if (isPM && hours < 12) hours += 12;
          if (!isPM && hours === 12) hours = 0;
        }

        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
      }

      // Try ISO format
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  };

  const handleDateChange = useCallback(
    (date: Date | null) => {
      const formattedDate = formatDateForOutput(date);
      setFieldValue(name, formattedDate);
    },
    [name, setFieldValue, outputFormat, showTimeSelect],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setFieldValue(name, null);
      setFieldTouched(name, true);
      setShowTimeDropdown(false);
    },
    [name, setFieldValue, setFieldTouched],
  );

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
    setIsFocus(false);
  }, [name, setFieldTouched]);

  const handleFocus = useCallback(() => {
    setIsFocus(true);
  }, []);

  const handleCalendarClose = useCallback(() => {
    setIsFocus(false);
    setShowTimeDropdown(false);
    handleBlur();
  }, [handleBlur]);

  const handleWrapperClick = useCallback(() => {
    if (!disabled && datepickerRef.current) {
      datepickerRef.current.setFocus();
      // Force the calendar to open
      const input = datepickerRef.current.input;
      if (input) {
        input.focus();
        input.click();
      }
    }
  }, [disabled]);

  const handleTimeDropdownToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTimeDropdown((prev) => !prev);
  }, []);

  // Generate time options for custom dropdown
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += timeIntervals) {
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        options.push(time);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleTimeSelect = useCallback(
    (time: Date) => {
      const currentDate = parseInputDate(field.value) || new Date();
      const newDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        time.getHours(),
        time.getMinutes(),
      );
      handleDateChange(newDate);
      setShowTimeDropdown(false);
    },
    [field.value, handleDateChange],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTimeDropdown(false);
      }
    };

    if (showTimeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTimeDropdown]);

  const getBorderColor = () => {
    if (hasError) return orange.orange500;
    if (isFocus) return orange.orange400;
    if (disabled) return "#e2e8f0";
    return "#cbd5e1";
  };

  const getBackgroundColor = () => {
    return disabled ? "#f8fafc" : "#ffffff";
  };

  const getTextColor = () => {
    return disabled ? "#94a3b8" : "#334155";
  };

  // Parse the field value to Date object for DatePicker
  const selectedDate = parseInputDate(field.value);

  // Format display value
  const getDisplayValue = () => {
    if (!field.value) return "";

    const date = parseInputDate(field.value);
    if (!date) return field.value;

    if (showDateSelect && showTimeSelect) {
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (showDateSelect) {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } else if (showTimeSelect) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: timeFormat.includes("aa") || timeFormat.includes("hh"),
      });
    }

    return field.value;
  };

  const displayValue = getDisplayValue();

  // Format time for display in dropdown
  const formatTimeForDisplay = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: timeFormat.includes("aa") || timeFormat.includes("hh"),
    });
  };

  return (
    <StyleFormWrapper styleWrapper={styleWrapper}>
      {label && (
        <StyleLabel hasError={hasError} styleLabel={styleLabel}>
          {label}
          {required && (
            <span style={{ color: orange.orange500, marginLeft: 4 }}>*</span>
          )}
        </StyleLabel>
      )}

      <StyleInputWrapper
        ref={wrapperRef}
        style={{
          border: `1px solid ${getBorderColor()}`,
          borderRadius: "8px",
          padding: "10px 12px",
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: getBackgroundColor(),
          display: "flex",
          alignItems: "center",
          gap: "10px",
          position: "relative",
          transition: "all 0.2s ease-in-out",
          boxShadow: isFocus ? `0 0 0 3px ${orange.orange100}` : "none",
        }}
        onClick={handleWrapperClick}
      >
        {/* Icon based on selection type */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            color: getTextColor(),
            flexShrink: 0,
          }}
        >
          {showTimeSelect && !showDateSelect ? (
            <Clock size={18} />
          ) : (
            <Calendar size={18} />
          )}
        </div>

        {/* Display value or placeholder */}
        <div
          style={{
            flex: 1,
            color: displayValue ? getTextColor() : "#94a3b8",
            fontSize: "14px",
            fontWeight: displayValue ? 500 : 400,
            minHeight: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {displayValue || getPlaceholder()}
        </div>

        {/* Time dropdown toggle for time selection */}
        {showTimeSelect && !disabled && (
          <div style={{ position: "relative" }} ref={timeDropdownRef}>
            <button
              type="button"
              onClick={handleTimeDropdownToggle}
              style={{
                background: "none",
                border: "none",
                padding: "4px",
                borderRadius: "4px",
                cursor: "pointer",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {showTimeDropdown ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>

            {/* Time dropdown */}
            {showTimeDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "4px",
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 1001,
                  minWidth: "120px",
                }}
              >
                <div
                  style={{
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: orange.orange600,
                    borderBottom: "1px solid #f1f5f9",
                    backgroundColor: orange.orange50,
                  }}
                >
                  Select Time
                </div>
                {timeOptions.map((time, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTimeSelect(time)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: "14px",
                      textAlign: "left",
                      borderBottom: "1px solid #f1f5f9",
                      color:
                        selectedDate &&
                        selectedDate.getHours() === time.getHours() &&
                        selectedDate.getMinutes() === time.getMinutes()
                          ? orange.orange600
                          : "#334155",
                      fontWeight:
                        selectedDate &&
                        selectedDate.getHours() === time.getHours() &&
                        selectedDate.getMinutes() === time.getMinutes()
                          ? "600"
                          : "400",
                      backgroundColor:
                        selectedDate &&
                        selectedDate.getHours() === time.getHours() &&
                        selectedDate.getMinutes() === time.getMinutes()
                          ? orange.orange50
                          : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (
                        !(
                          selectedDate &&
                          selectedDate.getHours() === time.getHours() &&
                          selectedDate.getMinutes() === time.getMinutes()
                        )
                      ) {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (
                        !(
                          selectedDate &&
                          selectedDate.getHours() === time.getHours() &&
                          selectedDate.getMinutes() === time.getMinutes()
                        )
                      ) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {formatTimeForDisplay(time)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Clear button */}
        {!required && field.value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: "none",
              border: "none",
              padding: "4px",
              borderRadius: "4px",
              cursor: "pointer",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f1f5f9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <X size={16} />
          </button>
        )}

        {/* Hidden but interactive DatePicker */}
        <DatePicker
          ref={datepickerRef}
          selected={selectedDate}
          onChange={handleDateChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onCalendarClose={handleCalendarClose}
          placeholderText=""
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelect && !showDateSelect}
          timeIntervals={timeIntervals}
          timeFormat={timeFormat}
          dateFormat={getDateFormat()}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          className="custom-datepicker-input"
          calendarClassName="custom-calendar"
          popperClassName="custom-popper"
          autoComplete="off"
          isClearable={false}
          showPopperArrow={false}
          popperPlacement="bottom-start"
          shouldCloseOnSelect={!showTimeSelect}
          timeCaption={timeCaption}
        />
      </StyleInputWrapper>

      {hasError && <ErrorMessage message={meta.error as string} />}

      <style>{`
        .custom-datepicker-input {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          border: none;
          background: transparent;
        }

        .custom-datepicker-input:focus {
          outline: none;
        }

        .custom-calendar {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          border: 1px solid #e2e8f0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          transform-origin: top left;
        }

        .custom-calendar .react-datepicker__header {
          background-color: ${orange.orange500};
          color: #fff;
          border-bottom: none;
          padding: 16px;
        }

        .custom-calendar .react-datepicker__current-month {
          color: white;
          font-weight: 600;
          font-size: 16px;
          margin-bottom: 8px;
        }
        
        .custom-calendar .react-datepicker__day-name {
          color: white;
          font-weight: 500;
          font-size: 12px;
        }

        .custom-calendar .react-datepicker__day {
          border-radius: 6px;
          margin: 2px;
          font-weight: 400;
          transition: all 0.2s ease;
        }

        .custom-calendar .react-datepicker__day:hover {
          background-color: ${orange.orange100};
          border-radius: 6px;
        }

        .custom-calendar .react-datepicker__day--selected,
        .custom-calendar .react-datepicker__day--keyboard-selected {
          background-color: ${orange.orange500} !important;
          color: white !important;
          border-radius: 6px;
          font-weight: 600;
        }

        .custom-calendar .react-datepicker__day--today {
          background-color: ${orange.orange100};
          color: ${orange.orange700};
          border-radius: 6px;
          font-weight: 600;
        }

        .custom-calendar .react-datepicker__time-container {
          border-left: 1px solid #e2e8f0;
          width: 120px;
        }

        .custom-calendar .react-datepicker__time-box {
          width: 100% !important;
        }

        .custom-calendar .react-datepicker__time-list-item {
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 400;
          transition: all 0.2s ease;
        }

        .custom-calendar .react-datepicker__time-list-item:hover {
          background-color: #f8fafc;
        }

        .custom-calendar .react-datepicker__time-list-item--selected {
          background-color: ${orange.orange500} !important;
          color: white !important;
          font-weight: 600;
        }

        .custom-popper {
          z-index: 1000;
        }

        /* Ensure the popup aligns properly with the input */
        .react-datepicker-popper[data-placement^="bottom"] {
          padding-top: 8px !important;
        }

        .react-datepicker-wrapper {
          display: block !important;
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }

        @media (max-width: 480px) {
          .custom-calendar {
            font-size: 14px;
            transform: scale(0.9);
          }
          
          .react-datepicker__month-container {
            float: none;
          }
          
          .react-datepicker__time-container {
            width: 100%;
            border-left: none;
            border-top: 1px solid #e2e8f0;
          }

          /* Center the calendar on mobile */
          .custom-popper {
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
        }
      `}</style>
    </StyleFormWrapper>
  );
};

export default DateTimePicker;
